// --- src/components/SupportModal.tsx ---
import React, { useState, useRef, useEffect } from 'react';
import { supabaseService } from '../services/supabaseService';
import { LifeBuoy, X, Send, MessageCircle, CheckCircle, Book } from 'lucide-react';
import { HelpCenter } from './HelpCenter';
import { toast } from 'react-hot-toast';

export const SupportModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'help' | 'ticket'>('help');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Form state
  const [page, setPage] = useState('Áttekintés');
  const [process, setProcess] = useState('Egyéb');
  const [description, setDescription] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Timeout ref for submit handling (memory leak prevention)
  const submitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount to prevent memory leak
  useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
    };
  }, []);

  // Cleanup timeout when modal closes
  useEffect(() => {
    if (!isOpen && submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current);
      submitTimeoutRef.current = null;
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await supabaseService.submitTicket({
        page,
        process,
        description,
        error: errorMsg,
      });
      setSuccess(true);
      // 3 másodperc múlva bezárjuk
      // Clear any pending submit timeout to prevent memory leak
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
      submitTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
        setSuccess(false);
        setDescription('');
        setErrorMsg('');
        submitTimeoutRef.current = null;
      }, 3000);
    } catch (err: any) {
      toast.error('Nem sikerült elküldeni a hibajegyet. Kérlek próbáld újra!');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSuccess(false);
    setDescription('');
    setErrorMsg('');
    setActiveTab('help'); // Reset to help tab when closing
  };

  return (
    <>
      {/* LEBEGŐ GOMB (Jobb alul) */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-slate-800 text-white p-4 rounded-full shadow-xl hover:bg-slate-700 transition z-50 flex items-center gap-2 group"
        title="Hibabejelentés / Segítség"
      >
        <LifeBuoy size={24} />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap">
          Segítségkérés
        </span>
      </button>

      {/* MODAL ABLAK */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div
            className={`bg-card rounded-xl shadow-2xl w-full ${activeTab === 'help' ? 'max-w-4xl' : 'max-w-lg'} overflow-hidden border border-gray-200 dark:border-gray-700 max-h-[90vh] flex flex-col`}
          >
            {/* Fejléc */}
            <div className="bg-slate-800 dark:bg-gray-900 p-4 flex justify-between items-center text-white">
              <h3 className="font-bold flex items-center gap-2">
                <LifeBuoy size={20} /> Segítség & Támogatás
              </h3>
              <button
                onClick={handleClose}
                className="hover:bg-slate-600 dark:hover:bg-gray-700 p-1 rounded text-white"
                aria-label="Bezárás"
              >
                <X />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setActiveTab('help')}
                className={`flex-1 py-3 px-4 text-center font-medium transition ${
                  activeTab === 'help'
                    ? 'border-b-2 border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <Book size={18} className="inline-block mr-2" />
                Segítség
              </button>
              <button
                onClick={() => setActiveTab('ticket')}
                className={`flex-1 py-3 px-4 text-center font-medium transition ${
                  activeTab === 'ticket'
                    ? 'border-b-2 border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <MessageCircle size={18} className="inline-block mr-2" />
                Hibajegy
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {activeTab === 'help' ? (
                <HelpCenter />
              ) : (
                <>
                  {success ? (
                    <div className="p-10 text-center bg-card">
                      <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle size={32} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        Köszönjük!
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 mt-2">
                        A hibajegyet rögzítettük. Hamarosan jelentkezünk.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="p-6 space-y-4 bg-card">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-gray-500 dark:text-gray-300 uppercase mb-1">
                            Melyik oldalon?
                          </label>
                          <select
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-accent outline-none"
                            value={page}
                            onChange={(e) => setPage(e.target.value)}
                          >
                            <option>Áttekintés / Dashboard</option>
                            <option>Email Generátor</option>
                            <option>Adatbázis Import</option>
                            <option>Leltár / Szkennelés</option>
                            <option>Eredmény / History</option>
                            <option>Beállítások</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-500 dark:text-gray-300 uppercase mb-1">
                            Folyamat
                          </label>
                          <select
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-accent outline-none"
                            value={process}
                            onChange={(e) => setProcess(e.target.value)}
                          >
                            <option>Egyéb / Nem tudom</option>
                            <option>Fájl feltöltés</option>
                            <option>Vonalkód leolvasás</option>
                            <option>AI Elemzés</option>
                            <option>Mentés / Törlés</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-gray-300 uppercase mb-1">
                          Mi a probléma? (Részletesen)
                        </label>
                        <textarea
                          required
                          rows={4}
                          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-accent outline-none text-sm"
                          placeholder="Pl.: Amikor rákattintok a mentés gombra, nem történik semmi, pedig..."
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-gray-300 uppercase mb-1">
                          Hibaüzenet (Ha volt)
                        </label>
                        <input
                          type="text"
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 font-mono text-xs placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 outline-none"
                          placeholder="Pl.: Error 404 vagy Network Error"
                          value={errorMsg}
                          onChange={(e) => setErrorMsg(e.target.value)}
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-blue-600 dark:bg-blue-500 text-white py-3 rounded-lg font-bold hover:bg-blue-700 dark:hover:bg-blue-600 transition flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {loading ? (
                          'Küldés...'
                        ) : (
                          <>
                            <Send size={18} /> Hibajegy Beküldése
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
